import { HTMLAttributes, Ref } from "react"
import { useBottomNavVisible } from "@/hooks/use-bottom-nav-visible"
import { cn } from "cn"

function StickyBottomBar({
  ref,
  inModal,
  variant,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  inModal?: boolean
  /** Mobile: glass pill floating above the site nav, like the chat composer. */
  variant?: "frosted"
  ref?: Ref<HTMLDivElement>
}) {
  const bottomNavVisible = useBottomNavVisible()
  const frosted = variant === "frosted"

  return (
    <div
      ref={ref}
      // Unconditional on purpose: the probe only counts the bar when it is
      // actually painted at the edge, so `sm:static`, unpinned and in-modal
      // cases need no gate here.
      data-bottom-edge=""
      className={cn(
        "sticky bottom-0 z-1 shrink-0 transition-transform duration-drawer",
        frosted &&
          "max-sm:mx-2 max-sm:rounded-4xl max-sm:p-2 max-sm:glass-bar sm:rounded-t-2xl sm:bg-background/80 sm:py-6 sm:shadow-[0_8px_26px_0_rgba(0,0,0,0.15)] sm:backdrop-blur-md sm:dark:border-t",
        className,
        // Full nav height when shown; safe-area when hidden.
        // Safe-area offset is mobile-only — BottomStack (and the nav) don't exist on sm+.
        !inModal &&
          !frosted &&
          (bottomNavVisible
            ? "-translate-y-(--bottom-nav-height)"
            : "max-sm:-translate-y-[env(safe-area-inset-bottom)]"),
        // Same resting spots as a composer in BottomStack: a gap above the nav
        // pill when shown, the pill's own inset when hidden.
        !inModal &&
          frosted &&
          (bottomNavVisible
            ? "-translate-y-[calc(var(--bottom-nav-pill)+var(--bottom-stack-gap)+env(safe-area-inset-bottom)+var(--bottom-edge-gap))]"
            : "max-sm:-translate-y-[calc(env(safe-area-inset-bottom)+var(--bottom-edge-gap))]"),
        // The translate above lifts the bar off the viewport bottom, leaving a
        // transparent safe-area strip below it that page content shows through.
        // Paint it with an `after` backdrop matching the bar; `not-empty` keeps unfilled portal
        // targets from painting a floating strip. The height must animate on
        // the same clock as the bar's translate: Safari changes the env() inset
        // instantly when its chrome collapses/expands, and an instant-height
        // strip under a still-transitioning bar opens a see-through seam.
        !inModal &&
          !frosted &&
          "max-sm:not-empty:after:absolute max-sm:not-empty:after:inset-x-0 max-sm:not-empty:after:top-full max-sm:not-empty:after:h-[env(safe-area-inset-bottom)] max-sm:not-empty:after:bg-background max-sm:not-empty:after:transition-[height] max-sm:not-empty:after:duration-drawer"
      )}
      {...props}
    />
  )
}

StickyBottomBar.displayName = "StickyBottomBar"

export { StickyBottomBar }
